<template>
    <div id="new-google-drive-channel-form">
        <a-form>
            <div class="form-wrapper">
                <div class="input-wrapper">
                    <label>{{ $t("googleDrive.channelName") }}</label>
                    <a-form-item
                        :validate-status="getErrorStatus('channelName')"
                        :help="getErrorMessage('channelName')"
                    >
                        <a-input
                            v-model="form.channelName"
                            :placeholder="$t('googleDrive.channelName')"
                            name="channelName"
                            ref="channelName"
                        />
                    </a-form-item>
                </div>

                <div class="oauth-credentials-section">
                    <a-alert type="info" show-icon class="mb-3">
                        <template #message>
                            <strong>{{ $t("googleDrive.byocTitle") }}</strong>
                        </template>
                        <template #description>
                            <div>{{ $t("googleDrive.byocDescription") }}</div>
                            <a href="https://console.cloud.google.com/apis/credentials" target="_blank" class="mt-2 d-block">
                                <i class="material-icons" style="font-size: 14px; vertical-align: middle;">open_in_new</i>
                                Google Cloud Console
                            </a>
                        </template>
                    </a-alert>

                    <div class="input-wrapper">
                        <label>{{ $t("googleDrive.clientId") }} <span class="required">*</span></label>
                        <a-form-item
                            :validate-status="getErrorStatus('clientId')"
                            :help="getErrorMessage('clientId')"
                        >
                            <a-input
                                v-model="form.clientId"
                                :placeholder="$t('googleDrive.clientIdPlaceholder')"
                                name="clientId"
                                ref="clientId"
                            />
                        </a-form-item>
                    </div>

                    <div class="input-wrapper">
                        <label>{{ $t("googleDrive.clientSecret") }} <span class="required">*</span></label>
                        <a-form-item
                            :validate-status="getErrorStatus('clientSecret')"
                            :help="getErrorMessage('clientSecret')"
                        >
                            <a-input-password
                                v-model="form.clientSecret"
                                :placeholder="$t('googleDrive.clientSecretPlaceholder')"
                                name="clientSecret"
                                ref="clientSecret"
                            />
                        </a-form-item>
                    </div>

                    <a-collapse class="setup-guide">
                        <a-collapse-panel :header="$t('googleDrive.setupGuideTitle')">
                            <ol class="setup-steps">
                                <li>{{ $t("googleDrive.setupStep1") }}</li>
                                <li>{{ $t("googleDrive.setupStep2") }}</li>
                                <li>{{ $t("googleDrive.setupStep3") }}</li>
                                <li>
                                    {{ $t("googleDrive.setupStep4") }}
                                    <code class="redirect-uri">{{ redirectUri }}</code>
                                    <a-button size="small" type="link" @click="copyRedirectUri">
                                        <i class="material-icons" style="font-size: 14px;">content_copy</i>
                                    </a-button>
                                </li>
                                <li>{{ $t("googleDrive.setupStep5") }}</li>
                                <li>{{ $t("googleDrive.setupStep6") }}</li>
                            </ol>
                        </a-collapse-panel>
                    </a-collapse>
                </div>

                <div class="connect-body">
                    <div class="requirements">
                        <div><i class="material-icons">info</i>{{ $t("googleDrive.ensureGoogleAccount") }}</div>
                        <div><i class="material-icons">info</i>{{ $t("googleDrive.ensureGoogleDriveAccess") }}</div>
                    </div>

                    <strong> {{ $t("googleDrive.permissionDesc") }} </strong>

                    <div>
                        <a-checkbox :checked="termOfService" @change="acceptTermOfService('pp')"></a-checkbox>
                        <b
                            >{{ $t("search.integration.readAndAgree") }}
                            <a @click="acceptTermOfService('pp')">{{ $t("search.integration.privatePolicy") }}</a>
                            {{ $t("message.and") }}
                            <a @click="acceptTermOfService('tac')">{{
                                $t("search.integration.termsAndCondition")
                            }}</a></b
                        >
                    </div>
                </div>
            </div>
        </a-form>

        <div class="form-footer">
            <div class="actions">
                <SupsisButton ghost @click="handleCancel"> {{ $t("wp.cancel") }}</SupsisButton>
                <SupsisButton
                    primary
                    @click="handleSubmit"
                    :disabled="!termOfService || loading"
                    :loading="loading"
                >
                    {{ $t("googleDrive.connectToGoogleDrive") }}
                </SupsisButton>
            </div>
        </div>
        <TermOfService
            v-if="termOfServiceModal"
            :type="termOfServiceModal"
            @agree="acceptedTermOfService"
            @cancel="termOfServiceModal = null"
        />
    </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import _ from "lodash";
import CONFIG from "../../../../config/config";
import SupsisButton from "../../Settings/components/SupsisButton.vue";
import TermOfService from "../../../../components/TermOfService.vue";
import { socketMessageStream } from "../../../../utils/socketStream";
import { filter } from "rxjs/operators";

export default {
    name: "CreateGoogleDriveChannel",
    components: {
        TermOfService,
        SupsisButton,
    },
    data() {
        return {
            form: {
                channelName: "Google Drive",
                clientId: "",
                clientSecret: "",
            },
            loading: false,
            submitClicked: false,
            termOfService: false,
            termOfServiceModal: false,
            oauthWindow: null,
            cancels: [],
            checkWindowInterval: null,
        };
    },
    computed: {
        ...mapState({
            siteId: (state) => state.site.site._id,
            userId: (state) => state.auth.user._id || state.auth.adminUser._id,
        }),
        redirectUri() {
            return CONFIG.GOOGLE_DRIVE.REDIRECT_URI;
        },
    },
    methods: {
        ...mapActions({
            createChannel: "channel/createChannel",
            listChannels: "channel/listChannels",
        }),
        listenSocket() {
            const sub = socketMessageStream.pipe(filter((x) => x.cmd === "google_drive_success")).subscribe((x) => {
                this.$message.success(this.$t("googleDrive.connectedSuccess"));
                this.listChannels();
                this.loading = false;
                if (this.oauthWindow) {
                    this.oauthWindow.close();
                }
                if (this.checkWindowInterval) {
                    clearInterval(this.checkWindowInterval);
                }
                this.$emit("ok");
            });
            this.cancels.push(() => sub.unsubscribe());
        },
        acceptTermOfService(type) {
            this.termOfServiceModal = type || "pp";
        },
        acceptedTermOfService() {
            this.termOfServiceModal = null;
            this.termOfService = true;
        },
        getErrorStatus(fieldName) {
            return this.form[fieldName].length < 1 && this.submitClicked ? "error" : "";
        },
        getErrorMessage(fieldName) {
            if (this.form[fieldName].length < 1 && this.submitClicked) {
                if (fieldName === "clientId") return this.$t("googleDrive.clientIdRequired");
                if (fieldName === "clientSecret") return this.$t("googleDrive.clientSecretRequired");
                return this.$t("googleDrive.requiredChannel");
            }
            return "";
        },
        copyRedirectUri() {
            navigator.clipboard.writeText(this.redirectUri);
            this.$message.success(this.$t("googleDrive.redirectUriCopied"));
        },
        handleCancel() {
            this.$emit("cancel");
        },
        async handleSubmit() {
            this.submitClicked = true;
            if (this.form.channelName.length < 1) {
                this.$refs.channelName.focus();
                return;
            }
            if (this.form.clientId.length < 1) {
                this.$refs.clientId.focus();
                return;
            }
            if (this.form.clientSecret.length < 1) {
                this.$refs.clientSecret.focus();
                return;
            }

            this.loading = true;

            try {
                const channelData = {
                    type: "google-drive",
                    name: this.form.channelName,
                    payload: {
                        oauthCredentials: {
                            clientId: this.form.clientId,
                            clientSecret: this.form.clientSecret,
                        },
                    },
                    owner: this.userId,
                };

                const channel = await this.createChannel(channelData);
                this.startOauth(channel._id);
            } catch (err) {
                this.$message.error(this.$t("googleDrive.failedToCreateChannel"));
                this.loading = false;
            }
        },
        startOauth(channelId) {
            const { SCOPE, REDIRECT_URI, OAUTH2_ENDPOINT } = CONFIG.GOOGLE_DRIVE;
            const clientId = this.form.clientId;
            const state = `${this.siteId}/${channelId}`;

            const params = new URLSearchParams({
                client_id: clientId,
                redirect_uri: REDIRECT_URI,
                response_type: "code",
                scope: SCOPE,
                access_type: "offline",
                prompt: "consent",
                state: state,
            });

            const url = `${OAUTH2_ENDPOINT}?${params.toString()}`;

            const width = 600;
            const height = 700;
            const left = (window.screen.width - width) / 2;
            const top = (window.screen.height - height) / 2;
            const features = `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,status=yes`;

            this.oauthWindow = window.open(url, "GoogleDriveOAuth", features);

            this.checkWindowInterval = setInterval(() => {
                if (this.oauthWindow && this.oauthWindow.closed) {
                    clearInterval(this.checkWindowInterval);
                    this.checkWindowInterval = null;
                }
            }, 1000);

            setTimeout(() => {
                if (this.checkWindowInterval) {
                    clearInterval(this.checkWindowInterval);
                }
                if (this.loading) {
                    this.loading = false;
                    this.$message.warning(this.$t("googleDrive.oauthTimeout"));
                }
            }, 5 * 60 * 1000);
        },
    },
    mounted() {
        this.listenSocket();
    },
    beforeDestroy() {
        this.cancels.forEach((c) => c());
        if (this.oauthWindow && !this.oauthWindow.closed) {
            this.oauthWindow.close();
        }
        if (this.checkWindowInterval) {
            clearInterval(this.checkWindowInterval);
        }
    },
};
</script>

<style lang="scss" scoped>
#new-google-drive-channel-form {
    min-height: 250px;
    flex-direction: column;

    .form-wrapper {
        padding: 20px;
        display: flex;
        flex-direction: column;
        gap: 10px;

        .input-wrapper {
            display: flex;
            flex-direction: column;
            gap: 5px;
            height: 75px;
        }
    }

    .form-footer {
        display: flex;
        justify-content: flex-end;
        padding: 10px;
        margin-top: 20px;
        background: var(--color-bg-default);
        border-top: 1px solid var(--color-border-default);

        .actions {
            display: flex;
            gap: 10px;
        }
    }

    .connect-body {
        display: flex;
        flex-direction: column;
        gap: 5px;
        justify-content: center;

        .requirements {
            display: flex;
            flex-direction: column;
            gap: 5px;

            div {
                display: flex;
                gap: 5px;
                align-items: center;
                justify-content: flex-start;

                .material-icons {
                    color: var(--color-text-info-default);
                    font-size: 13px;
                }
            }
        }
    }

    .oauth-credentials-section {
        margin-bottom: 15px;

        .input-wrapper {
            margin-bottom: 10px;

            label {
                display: block;
                margin-bottom: 5px;
                font-weight: 500;

                .required {
                    color: #ff4d4f;
                }
            }
        }

        .setup-guide {
            margin-top: 15px;

            .setup-steps {
                padding-left: 20px;
                margin: 0;

                li {
                    margin-bottom: 8px;
                    line-height: 1.6;
                }

                .redirect-uri {
                    display: inline-block;
                    background: #f5f5f5;
                    padding: 2px 8px;
                    border-radius: 4px;
                    font-size: 12px;
                    word-break: break-all;
                }
            }
        }
    }

    .mb-3 {
        margin-bottom: 15px;
    }

    .mt-2 {
        margin-top: 10px;
    }

    .d-block {
        display: block;
    }
}
</style>
